import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import userService from '../../services/userService';
import './NotificationsSection.css';

const NotificationsSection = ({ notifications, onNotificationsUpdate }) => {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
    const navigate = useNavigate();

    const handleMarkAsRead = async (notificationId) => {
        try {
            await userService.markNotificationAsRead(notificationId);
            const updatedNotifications = notifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, is_read: true }
                    : notification
            );
            onNotificationsUpdate(updatedNotifications);
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await userService.markAllNotificationsAsRead();
            const updatedNotifications = (notifications || []).map(notification => ({
                ...notification,
                is_read: true
            }));
            onNotificationsUpdate(updatedNotifications);
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await userService.deleteNotification(notificationId);
            const updatedNotifications = (notifications || []).filter(
                notification => notification.id !== notificationId
            );
            onNotificationsUpdate(updatedNotifications);
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    const getNotificationIcon = (type) => {
        const icons = {
            class_reminder: 'fa-calendar',
            certificate_ready: 'fa-certificate',
            payment_due: 'fa-credit-card',
            default: 'fa-bell'
        };
        return icons[type] || icons.default;
    };

    const unreadCount = (notifications || []).filter(n => !n.is_read).length;

    return (
        <div className="notifications-section">
            <div className="section-header">
                <h2>{t('notifications.title')}</h2>
                {unreadCount > 0 && (
                    <button
                        className="btn btn-secondary"
                        onClick={handleMarkAllAsRead}
                    >
                        <i className="fas fa-check-double"></i>
                        {t('notifications.markAllRead')}
                    </button>
                )}
            </div>

            {(notifications || []).length > 0 ? (
                <div className="notifications-list">
                    {(notifications || []).map(notification => (
                        <div
                            key={notification.id}
                            className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                            onClick={() => navigate(`/notifications/${notification.id}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="notification-icon">
                                <i className={`fas ${getNotificationIcon(notification.type)}`}></i>
                            </div>
                            <div className="notification-content">
                                <div className="notification-header">
                                    <h3>{notification.title}</h3>
                                    <span className="notification-time">
                                        {new Date(notification.created_at).toLocaleString(dateLocale)}
                                    </span>
                                </div>
                            </div>
                            <div className="notification-actions">
                                {!notification.is_read && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkAsRead(notification.id);
                                        }}
                                    >
                                        <i className="fas fa-check"></i>
                                    </button>
                                )}
                                <button
                                    className="btn btn-danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(notification.id);
                                    }}
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <i className="fas fa-bell"></i>
                    <p>{t('notifications.emptyTitle')}</p>
                    <p className="empty-state-subtext">
                        {t('notifications.emptyDesc')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default NotificationsSection;
